import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { websocketService, NotificationMessage, WebSocketEventHandlers } from '../services/websocket';
import { useAuth } from './AuthContext';
import { logger } from '../utils/logger';

interface WebSocketContextType {
  isConnected: boolean;
  notifications: NotificationMessage[];
  unreadCount: number;
  connect: () => void;
  disconnect: () => void;
  sendMessage: (event: string, data: any) => void;
  joinRoom: (room: string) => void;
  leaveRoom: (room: string) => void;
  markNotificationAsRead: (notificationId: string) => void;
  markAllNotificationsAsRead: () => void;
  clearNotifications: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { user, isAuthenticated, token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<NotificationMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Connect to WebSocket when user is authenticated
  const connect = useCallback(() => {
    if (isAuthenticated && token) {
      logger.info('Connecting to WebSocket with user token');
      websocketService.connect(token);
    }
  }, [isAuthenticated, token]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    logger.info('Disconnecting from WebSocket');
    websocketService.disconnect();
  }, []);

  // Send message to WebSocket server
  const sendMessage = useCallback((event: string, data: any) => {
    websocketService.sendMessage(event, data);
  }, []);

  // Join a room
  const joinRoom = useCallback((room: string) => {
    websocketService.joinRoom(room);
  }, []);

  // Leave a room
  const leaveRoom = useCallback((room: string) => {
    websocketService.leaveRoom(room);
  }, []);

  // Mark notification as read
  const markNotificationAsRead = useCallback((notificationId: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true }
          : notification
      )
    );
  }, []);

  // Mark all notifications as read
  const markAllNotificationsAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Handle new notifications
  const handleNotification = useCallback((notification: NotificationMessage) => {
    setNotifications(prev => {
      // Check if notification already exists (avoid duplicates)
      const exists = prev.some(n => n.id === notification.id);
      if (exists) {
        return prev;
      }
      
      // Add new notification to the beginning of the list
      return [notification, ...prev];
    });
  }, []);

  // Handle connection status changes
  const handleConnectionChange = useCallback((connected: boolean) => {
    setIsConnected(connected);

    if (connected && user?.id) {
      // Join user-specific room when connected
      joinRoom(`user_${user.id}`);

      // Join role-specific rooms based on user's roles array
      if (user.roles && user.roles.length > 0) {
        user.roles.forEach(role => {
          joinRoom(`role_${role}`);
        });
      }

      // Join affiliate room if user has affiliate associations
      if (user.affiliates && user.affiliates.length > 0) {
        user.affiliates.forEach(affiliate => {
          joinRoom(`affiliate_${affiliate.affiliateId}`);
        });
      }
    }
  }, [user?.id, user?.roles, user?.affiliates, joinRoom]);

  // Handle WebSocket errors
  const handleError = useCallback((error: Error) => {
    logger.error('WebSocket error in context:', error);
    // You could show a toast notification here
  }, []);

  // Setup WebSocket event handlers
  useEffect(() => {
    const eventHandlers: WebSocketEventHandlers = {
      onNotification: handleNotification,
      onConnectionChange: handleConnectionChange,
      onError: handleError
    };

    websocketService.setEventHandlers(eventHandlers);

    return () => {
      websocketService.setEventHandlers({});
    };
  }, [handleNotification, handleConnectionChange, handleError]);

  // Connect/disconnect based on authentication status
  useEffect(() => {
    if (isAuthenticated && token) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, token, connect, disconnect]);

  // Update unread count when notifications change
  useEffect(() => {
    const unread = notifications.filter(n => !n.read).length;
    setUnreadCount(unread);
  }, [notifications]);

  // Auto-connect on mount if already authenticated
  useEffect(() => {
    if (isAuthenticated && token && !isConnected) {
      connect();
    }
  }, [isAuthenticated, token, isConnected, connect]);

  const contextValue: WebSocketContextType = {
    isConnected,
    notifications,
    unreadCount,
    connect,
    disconnect,
    sendMessage,
    joinRoom,
    leaveRoom,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    clearNotifications
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};
